import React, { useState, useRef } from 'react';
import { X, Plus, GripVertical } from 'lucide-react';

interface DraggableSkillsInputProps {
  mandatorySkills: string[];
  optionalSkills: string[];
  onMandatoryChange: (skills: string[]) => void;
  onOptionalChange: (skills: string[]) => void;
  error?: string;
}

interface SkillTagProps {
  skill: string;
  onRemove: () => void;
  isDragging?: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  type: 'mandatory' | 'optional';
}

const SkillTag: React.FC<SkillTagProps> = ({ 
  skill, 
  onRemove, 
  isDragging = false, 
  onDragStart, 
  onDragEnd,
  type 
}) => {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`
        inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
        transition-all duration-200 cursor-move select-none
        ${type === 'mandatory' 
          ? 'bg-primary-500/20 text-primary-700 border border-primary-300/50' 
          : 'bg-cyan-500/20 text-cyan-700 border border-cyan-300/50'
        }
        ${isDragging ? 'opacity-50 scale-95' : 'hover:scale-105 hover:shadow-md'}
        backdrop-blur-sm
      `}
    >
      <GripVertical className="w-3 h-3 text-gray-500" />
      <span>{skill}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 p-0.5 rounded-full hover:bg-red-500/20 transition-colors"
      >
        <X className="w-3 h-3 text-gray-500 hover:text-red-500" />
      </button>
    </div>
  );
};

export const DraggableSkillsInput: React.FC<DraggableSkillsInputProps> = ({
  mandatorySkills,
  optionalSkills,
  onMandatoryChange,
  onOptionalChange,
  error
}) => {
  const [newSkill, setNewSkill] = useState('');
  const [draggedSkill, setDraggedSkill] = useState<string | null>(null);
  const [dragOverType, setDragOverType] = useState<'mandatory' | 'optional' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const addSkill = (skill: string, type: 'mandatory' | 'optional') => {
    const trimmedSkill = skill.trim();
    if (!trimmedSkill) return;

    const targetArray = type === 'mandatory' ? mandatorySkills : optionalSkills;
    const otherArray = type === 'mandatory' ? optionalSkills : mandatorySkills;
    
    // Check if skill already exists in either array
    if (targetArray.includes(trimmedSkill) || otherArray.includes(trimmedSkill)) {
      return;
    }

    if (type === 'mandatory') {
      onMandatoryChange([...mandatorySkills, trimmedSkill]);
    } else {
      onOptionalChange([...optionalSkills, trimmedSkill]);
    }
    setNewSkill('');
  };

  const removeSkill = (skill: string, type: 'mandatory' | 'optional') => {
    if (type === 'mandatory') {
      onMandatoryChange(mandatorySkills.filter(s => s !== skill));
    } else {
      onOptionalChange(optionalSkills.filter(s => s !== skill));
    }
  };

  const handleDragStart = (e: React.DragEvent, skill: string) => {
    console.log('🚀 Drag start:', skill);
    setDraggedSkill(skill);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', skill);
  };

  const handleDragEnd = () => {
    setDraggedSkill(null);
    setDragOverType(null);
  };

  const handleDragOver = (e: React.DragEvent, type: 'mandatory' | 'optional') => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverType(type);
  };

  const handleDragLeave = () => {
    setDragOverType(null);
  };

  const handleDrop = (e: React.DragEvent, targetType: 'mandatory' | 'optional') => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedSkill = e.dataTransfer.getData('text/plain');
    console.log('🎯 Drop event triggered:', { droppedSkill, targetType, mandatorySkills, optionalSkills });
    
    if (!droppedSkill) {
      console.log('❌ No dropped skill data');
      return;
    }

    const sourceType = mandatorySkills.includes(droppedSkill) ? 'mandatory' : 'optional';
    console.log('📍 Source type:', sourceType, 'Target type:', targetType);
    
    if (sourceType === targetType) {
      console.log('⚠️ Same source and target, ignoring');
      return;
    }

    // Perform the move operation
    if (sourceType === 'mandatory' && targetType === 'optional') {
      // Moving from mandatory to optional
      console.log('🔄 Moving from mandatory to optional');
      const newMandatorySkills = mandatorySkills.filter(s => s !== droppedSkill);
      const newOptionalSkills = [...optionalSkills, droppedSkill];
      console.log('📤 New arrays:', { newMandatorySkills, newOptionalSkills });
      onMandatoryChange(newMandatorySkills);
      onOptionalChange(newOptionalSkills);
    } else if (sourceType === 'optional' && targetType === 'mandatory') {
      // Moving from optional to mandatory
      console.log('🔄 Moving from optional to mandatory');
      const newOptionalSkills = optionalSkills.filter(s => s !== droppedSkill);
      const newMandatorySkills = [...mandatorySkills, droppedSkill];
      console.log('📤 New arrays:', { newMandatorySkills, newOptionalSkills });
      onOptionalChange(newOptionalSkills);
      onMandatoryChange(newMandatorySkills);
    }

    setDraggedSkill(null);
    setDragOverType(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent, type: 'mandatory' | 'optional') => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addSkill(newSkill, type);
    }
  };

  return (
    <div className="space-y-6">
      {/* Mandatory Skills */}
      <div>
        <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
          Mandatory Skills *
        </label>
        <div
          className={`
            min-h-[120px] p-4 rounded-lg border-2 border-dashed transition-all duration-200
            ${dragOverType === 'mandatory' 
              ? 'border-primary-500/50 bg-primary-500/10' 
              : 'border-glass-border/50 bg-glass-surface/30'
            }
            hover:bg-glass-surface/40
          `}
          onDragOver={(e) => handleDragOver(e, 'mandatory')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'mandatory')}
        >
          {/* Skills Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {mandatorySkills.map((skill, index) => (
              <SkillTag
                key={`mandatory-${skill}-${index}`}
                skill={skill}
                type="mandatory"
                onRemove={() => removeSkill(skill, 'mandatory')}
                onDragStart={(e) => handleDragStart(e, skill)}
                onDragEnd={handleDragEnd}
                isDragging={draggedSkill === skill}
              />
            ))}
          </div>
          
          {/* Add Skill Input */}
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'mandatory')}
              placeholder="Add mandatory skill..."
              className="flex-1 p-2 bg-glass-surface/50 backdrop-blur-xl border border-glass-border/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary placeholder-text-enterprise-tertiary transition-all duration-300"
            />
            <button
              type="button"
              onClick={() => addSkill(newSkill, 'mandatory')}
              className="p-2 bg-primary-500/20 hover:bg-primary-500/30 text-primary-600 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        {error && <p className="text-text-enterprise-error text-sm mt-1">{error}</p>}
      </div>

      {/* Optional Skills */}
      <div>
        <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
          Optional Skills
        </label>
        <div
          className={`
            min-h-[120px] p-4 rounded-lg border-2 border-dashed transition-all duration-200
            ${dragOverType === 'optional' 
              ? 'border-cyan-500/50 bg-cyan-500/10' 
              : 'border-glass-border/50 bg-glass-surface/30'
            }
            hover:bg-glass-surface/40
          `}
          onDragOver={(e) => handleDragOver(e, 'optional')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'optional')}
        >
          {/* Skills Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {optionalSkills.map((skill, index) => (
              <SkillTag
                key={`optional-${skill}-${index}`}
                skill={skill}
                type="optional"
                onRemove={() => removeSkill(skill, 'optional')}
                onDragStart={(e) => handleDragStart(e, skill)}
                onDragEnd={handleDragEnd}
                isDragging={draggedSkill === skill}
              />
            ))}
          </div>
          
          {/* Add Skill Input */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'optional')}
              placeholder="Add optional skill..."
              className="flex-1 p-2 bg-glass-surface/50 backdrop-blur-xl border border-glass-border/50 rounded-lg focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 text-text-enterprise-primary placeholder-text-enterprise-tertiary transition-all duration-300"
            />
            <button
              type="button"
              onClick={() => addSkill(newSkill, 'optional')}
              className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-600 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
